import { useState } from 'react';
import BulkActionsBar from '../components/BulkActionsBar';
import PaginationControls, { paginateItems } from '../components/PaginationControls';
import { useApp } from '../context/AppContext';
import { persistAppendOnlyVersion, persistDeletedVersion, persistDeletedVersions } from '../lib/lumiAppendOnly';
import { gerarCodigo, nowISO } from '../utils';
import type { Cliente, Contrato } from '../types';

function Modal({ title, onClose, children }: {title: string;onClose: () => void;children: React.ReactNode;}) {
  return (
    <div className="modal-overlay" style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="modal-panel" style={{ width: 480, maxHeight: '90vh', overflowY: 'auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 className="modal-panel-title" style={{ fontSize: 15, fontWeight: 600 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#62666d', cursor: 'pointer', fontSize: 18 }}>X</button>
        </div>
        {children}
      </div>
    </div>);

}

function StatusBadge({ status }: {status: string;}) {
  const map: Record<string, {bg: string;color: string;}> = {
    ATIVO: { bg: 'rgba(52,211,153,0.12)', color: '#34d399' },
    INATIVO: { bg: 'rgba(156,163,175,0.12)', color: '#9ca3af' },
    ENCERRADO: { bg: 'rgba(156,163,175,0.12)', color: '#9ca3af' },
    SUSPENSO: { bg: 'rgba(251,191,36,0.12)', color: '#fbbf24' }
  };
  const s = map[status] || { bg: 'rgba(156,163,175,0.12)', color: '#9ca3af' };
  return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: s.bg, color: s.color }}>{status}</span>;
}

export default function Clientes() {
  const { clientes, setClientes, contratos, setContratos, unidades, addLog } = useApp();
  const [tab, setTab] = useState<'clientes' | 'contratos'>('clientes');
  const [modal, setModal] = useState<'cliente' | 'contrato' | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editDbId, setEditDbId] = useState<string | null>(null);
  const [toast, setToast] = useState('');
  const [form, setForm] = useState<Record<string, any>>({});
  const [erro, setErro] = useState('');
  const [saving, setSaving] = useState(false);
  const [clientesPage, setClientesPage] = useState(1);
  const [contratosPage, setContratosPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedClientes, setSelectedClientes] = useState<string[]>([]);
  const [selectedContratos, setSelectedContratos] = useState<string[]>([]);

  function showToast(msg: string) {setToast(msg);setTimeout(() => setToast(''), 3000);}
  function openNew(type: 'cliente' | 'contrato') {
    setForm(type === 'contrato' ? { codigo_contrato: gerarCodigo() } : {});
    setEditId(null);
    setEditDbId(null);
    setErro('');
    setModal(type);
  }
  function openEdit(type: 'cliente' | 'contrato', item: Cliente | Contrato) {
    setForm({ ...item });setEditId(item.id);setEditDbId((item as any)._id ?? item.id);setErro('');setModal(type);
  }
  function field(key: string, val: string | boolean) {setForm((prev) => ({ ...prev, [key]: val }));}

  async function saveCliente() {
    if (!form.razao_social?.trim()) {setErro('Razão social é obrigatória.');return;}
    if (!form.cnpj_cpf?.trim()) {setErro('CNPJ/CPF é obrigatório.');return;}
    setSaving(true);
    try {
      if (editId && editDbId) {
        const clienteAtual = clientes.find((c) => c.id === editId || (c as any)._id === editDbId);
        const atualizado = { ...clienteAtual, ...form, id: editId, updatedAt: nowISO() } as Cliente;
        await persistAppendOnlyVersion('clientes', atualizado as unknown as Record<string, unknown>);
        setClientes((prev) => prev.map((c) => c.id === editId || (c as any)._id === editDbId ? atualizado : c));
        addLog('CLIENTE', editId, 'EDITADO', `Cliente ${form.razao_social} editado`);
        showToast('Cliente atualizado.');
      } else {
        const novo: Omit<Cliente, '_id'> = {
          id: 'c' + Date.now(),
          razao_social: form.razao_social || '',
          nome_fantasia: form.nome_fantasia || '',
          cnpj_cpf: form.cnpj_cpf || '',
          email: form.email || '',
          telefone: form.telefone || '',
          contato_principal: form.contato_principal || '',
          endereco_logradouro: form.endereco_logradouro || '',
          endereco_numero: form.endereco_numero || '',
          endereco_complemento: form.endereco_complemento || '',
          endereco_bairro: form.endereco_bairro || '',
          endereco_cidade: form.endereco_cidade || '',
          endereco_estado: form.endereco_estado || '',
          endereco_cep: form.endereco_cep || '',
          status: form.status as Cliente['status'] || 'ATIVO',
          observacoes: form.observacoes,
          createdAt: nowISO()
        };
        await persistAppendOnlyVersion('clientes', novo as unknown as Record<string, unknown>);
        setClientes((prev) => [...prev, novo]);
        addLog('CLIENTE', novo.id, 'CRIADO', `Cliente ${novo.razao_social} cadastrado`);
        showToast('Cliente cadastrado.');
      }
      setModal(null);
    } catch {
      setErro('Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  }

  async function saveContrato() {
    if (!form.cliente_id) {setErro('Selecione o cliente.');return;}
    if (!form.unidade_id) {setErro('Selecione a unidade.');return;}
    if (!form.codigo_contrato?.trim()) {setErro('Numero do contrato e obrigatorio.');return;}
    if (!form.nome_contrato?.trim()) {setErro('Nome do contrato é obrigatório.');return;}
    setSaving(true);
    try {
      if (editId && editDbId) {
        const contratoAtual = contratos.find((c) => c.id === editId || (c as any)._id === editDbId);
        const atualizado = { ...contratoAtual, ...form, id: editId, updatedAt: nowISO() } as Contrato;
        await persistAppendOnlyVersion('contratos', atualizado as unknown as Record<string, unknown>);
        setContratos((prev) => prev.map((c) => c.id === editId || (c as any)._id === editDbId ? atualizado : c));
        addLog('CONTRATO', editId, 'EDITADO', `Contrato ${form.nome_contrato} editado`);
        showToast('Contrato atualizado.');
      } else {
        const novo: Omit<Contrato, '_id'> = {
          id: 'con' + Date.now(),
          cliente_id: form.cliente_id || '',
          unidade_id: form.unidade_id || '',
          codigo_contrato: form.codigo_contrato || gerarCodigo(),
          nome_contrato: form.nome_contrato || '',
          objeto_contrato: form.objeto_contrato || '',
          data_inicio: form.data_inicio || '',
          data_fim: form.data_fim || '',
          valor_contratado: Number(form.valor_contratado) || 0,
          status: form.status as Contrato['status'] || 'ATIVO',
          observacoes: form.observacoes,
          createdAt: nowISO()
        };
        await persistAppendOnlyVersion('contratos', novo as unknown as Record<string, unknown>);
        setContratos((prev) => [...prev, novo]);
        addLog('CONTRATO', novo.id, 'CRIADO', `Contrato ${novo.nome_contrato} cadastrado`);
        showToast('Contrato cadastrado.');
      }
      setModal(null);
    } catch {
      setErro('Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  }

  async function deleteCliente(cliente: Cliente, pedirConfirmacao = true) {
    const contratosVinculados = contratos.filter((c) => c.cliente_id === cliente.id || c.cliente_id === (cliente as any)._id);
    const mensagem = contratosVinculados.length > 0 ?
    `Excluir o cliente ${cliente.razao_social}? Tambem serao excluidos ${contratosVinculados.length} contrato(s) vinculado(s).` :
    `Excluir o cliente ${cliente.razao_social}? Esta acao nao pode ser desfeita.`;
    if (pedirConfirmacao) {
      const confirmed = window.confirm(mensagem);
      if (!confirmed) return;
    }

    setSaving(true);
    try {
      await persistDeletedVersions('contratos', contratosVinculados as unknown as Record<string, unknown>[]);
      await persistDeletedVersion('clientes', cliente as unknown as Record<string, unknown>);
      setContratos((prev) => prev.filter((c) => !contratosVinculados.some((v) => v.id === c.id || (v as any)._id === (c as any)._id)));
      setClientes((prev) => prev.filter((c) => c.id !== cliente.id && (c as any)._id !== (cliente as any)._id));
      addLog('CLIENTE', cliente.id, 'CANCELADO', `Cliente ${cliente.razao_social} excluido com ${contratosVinculados.length} contrato(s) vinculado(s)`);
      showToast('Cliente excluido.');
    } catch {
      setErro('Erro, tente novamente mais tarde.');
    } finally {
      setSaving(false);
    }
  }

  async function deleteContrato(contrato: Contrato, pedirConfirmacao = true) {
    if (pedirConfirmacao) {
      const confirmed = window.confirm(`Excluir o contrato ${contrato.nome_contrato}? Esta acao nao pode ser desfeita.`);
      if (!confirmed) return;
    }

    setSaving(true);
    try {
      await persistDeletedVersion('contratos', contrato as unknown as Record<string, unknown>);
      setContratos((prev) => prev.filter((c) => c.id !== contrato.id && (c as any)._id !== (contrato as any)._id));
      addLog('CONTRATO', contrato.id, 'CANCELADO', `Contrato ${contrato.nome_contrato} excluido`);
      showToast('Contrato excluido.');
    } catch {
      setErro('Erro, tente novamente mais tarde.');
    } finally {
      setSaving(false);
    }
  }

  async function deleteSelectedClientes() {
    const selected = clientes.filter((cliente) => selectedClientes.includes((cliente as any)._id ?? cliente.id));
    if (selected.length === 0) return;
    const confirmed = window.confirm(`Excluir ${selected.length} cliente(s) selecionado(s)? Contratos vinculados tambem serao removidos.`);
    if (!confirmed) return;
    for (const cliente of selected) {
      await deleteCliente(cliente, false);
    }
    setSelectedClientes([]);
  }

  async function deleteSelectedContratos() {
    const selected = contratos.filter((contrato) => selectedContratos.includes((contrato as any)._id ?? contrato.id));
    if (selected.length === 0) return;
    const confirmed = window.confirm(`Excluir ${selected.length} contrato(s) selecionado(s)?`);
    if (!confirmed) return;
    for (const contrato of selected) {
      await deleteContrato(contrato, false);
    }
    setSelectedContratos([]);
  }

  const paginatedClientes = paginateItems(clientes, clientesPage, pageSize);
  const paginatedContratos = paginateItems(contratos, contratosPage, pageSize);

  const inp = (label: string, key: string, type = 'text', disabled = false) =>
  <div style={{ marginBottom: 12 }}>
      <label className="label-field">{label}</label>
      <input className="input-field" type={type} value={(form as Record<string, string>)[key] || ''} onChange={(e) => field(key, e.target.value)} disabled={disabled} style={disabled ? { opacity: 0.5 } : {}} />
    </div>;


  const sel = (label: string, key: string, options: {value: string;label: string;}[]) =>
  <div style={{ marginBottom: 12 }}>
      <label className="label-field">{label}</label>
      <select className="input-field" value={(form as Record<string, string>)[key] || ''} onChange={(e) => field(key, e.target.value)}>
        <option value="">Selecione...</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>;


  return (
    <div>
      {toast && <div style={{ position: 'fixed', top: 20, right: 20, background: '#1a2e1a', border: '1px solid #34d399', color: '#34d399', padding: '10px 16px', borderRadius: 8, zIndex: 100, fontSize: 13 }}>{toast}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ color: '#f7f8f8', fontSize: 18, fontWeight: 600 }}>Clientes e Contratos</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-ghost" onClick={() => openNew('cliente')}>+ Cliente</button>
          <button className="btn-primary" onClick={() => openNew('contrato')}>+ Contrato</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {(['clientes', 'contratos'] as const).map((t) =>
        <button key={t} onClick={() => setTab(t)} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: tab === t ? 500 : 400, background: tab === t ? 'rgba(240,64,59,0.2)' : 'rgba(255,255,255,0.04)', color: tab === t ? '#ffb4b1' : '#8a8f98' }}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        )}
      </div>

      {tab === 'clientes' &&
      <div className="card">
          {clientes.length === 0 ?
        <div style={{ padding: 40, textAlign: 'center', color: '#62666d', fontSize: 13 }}>Nenhum cliente cadastrado. <button className="btn-ghost" style={{ marginLeft: 8 }} onClick={() => openNew('cliente')}>Cadastrar agora</button></div> :

        <>
            <BulkActionsBar selectedCount={selectedClientes.length} onClear={() => setSelectedClientes([])} onDelete={() => void deleteSelectedClientes()} disabled={saving} />
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <th style={{ width: 36, padding: '10px 16px' }} />
                {['Razão Social', 'Nome Fantasia', 'CNPJ/CPF', 'Contato', 'Status', ''].map((h) => <th key={h} style={{ color: '#62666d', fontSize: 11, padding: '10px 16px', textAlign: 'left', fontWeight: 500 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {paginatedClientes.items.map((c) =>
              <tr key={(c as any)._id ?? c.id} className="table-row-hover" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '10px 16px' }}>
                      <input type="checkbox" checked={selectedClientes.includes((c as any)._id ?? c.id)} onChange={(event) => setSelectedClientes((prev) => event.target.checked ? [...prev, (c as any)._id ?? c.id] : prev.filter((id) => id !== ((c as any)._id ?? c.id)))} />
                    </td>
                    <td style={{ padding: '10px 16px', color: '#d0d6e0', fontSize: 13 }}>{c.razao_social}</td>
                    <td style={{ padding: '10px 16px', color: '#8a8f98', fontSize: 13 }}>{c.nome_fantasia}</td>
                    <td style={{ padding: '10px 16px', color: '#8a8f98', fontSize: 13 }}>{c.cnpj_cpf}</td>
                    <td style={{ padding: '10px 16px', color: '#8a8f98', fontSize: 13 }}>{c.contato_principal}</td>
                    <td style={{ padding: '10px 16px' }}><StatusBadge status={c.status} /></td>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => openEdit('cliente', c)}>Editar</button>
                        <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12, color: '#f87171' }} onClick={() => deleteCliente(c)} disabled={saving}>Excluir</button>
                      </div>
                    </td>
                  </tr>
              )}
              </tbody>
            </table>
            <PaginationControls page={paginatedClientes.safePage} pageSize={pageSize} totalItems={clientes.length} onPageChange={setClientesPage} onPageSizeChange={setPageSize} />
            </>
        }
        </div>
      }

      {tab === 'contratos' &&
      <div className="card">
          {contratos.length === 0 ?
        <div style={{ padding: 40, textAlign: 'center', color: '#62666d', fontSize: 13 }}>Nenhum contrato cadastrado. <button className="btn-ghost" style={{ marginLeft: 8 }} onClick={() => openNew('contrato')}>Cadastrar agora</button></div> :

        <>
            <BulkActionsBar selectedCount={selectedContratos.length} onClear={() => setSelectedContratos([])} onDelete={() => void deleteSelectedContratos()} disabled={saving} />
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <th style={{ width: 36, padding: '10px 16px' }} />
                {['Código', 'Nome', 'Cliente', 'Período', 'Valor', 'Status', ''].map((h) => <th key={h} style={{ color: '#62666d', fontSize: 11, padding: '10px 16px', textAlign: 'left', fontWeight: 500 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {paginatedContratos.items.map((c) => {
                const cli = clientes.find((x) => x.id === c.cliente_id || (x as any)._id === c.cliente_id);
                return (
                  <tr key={(c as any)._id ?? c.id} className="table-row-hover" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td style={{ padding: '10px 16px' }}>
                        <input type="checkbox" checked={selectedContratos.includes((c as any)._id ?? c.id)} onChange={(event) => setSelectedContratos((prev) => event.target.checked ? [...prev, (c as any)._id ?? c.id] : prev.filter((id) => id !== ((c as any)._id ?? c.id)))} />
                      </td>
                      <td style={{ padding: '10px 16px', color: '#8a8f98', fontSize: 12 }}>{c.codigo_contrato}</td>
                      <td style={{ padding: '10px 16px', color: '#d0d6e0', fontSize: 13 }}>{c.nome_contrato}</td>
                      <td style={{ padding: '10px 16px', color: '#8a8f98', fontSize: 13 }}>{cli?.nome_fantasia || cli?.razao_social}</td>
                      <td style={{ padding: '10px 16px', color: '#8a8f98', fontSize: 12 }}>{c.data_inicio} â†’ {c.data_fim}</td>
                      <td style={{ padding: '10px 16px', color: '#34d399', fontSize: 13 }}>R$ {Number(c.valor_contratado).toLocaleString('pt-BR')}</td>
                      <td style={{ padding: '10px 16px' }}><StatusBadge status={c.status} /></td>
                      <td style={{ padding: '10px 16px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => openEdit('contrato', c)}>Editar</button>
                          <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12, color: '#f87171' }} onClick={() => deleteContrato(c)} disabled={saving}>Excluir</button>
                        </div>
                      </td>
                    </tr>);

              })}
              </tbody>
            </table>
            <PaginationControls page={paginatedContratos.safePage} pageSize={pageSize} totalItems={contratos.length} onPageChange={setContratosPage} onPageSizeChange={setPageSize} />
            </>
        }
        </div>
      }

      {modal === 'cliente' &&
      <Modal title={editId ? 'Editar Cliente' : 'Novo Cliente'} onClose={() => setModal(null)}>
          {inp('Razão Social *', 'razao_social')}
          {inp('Nome Fantasia', 'nome_fantasia')}
          {inp('CNPJ/CPF *', 'cnpj_cpf')}
          {inp('E-mail', 'email', 'email')}
          {inp('Telefone', 'telefone')}
          {inp('Contato Principal', 'contato_principal')}
          <div style={{ margin: '4px 0 12px', paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <p style={{ color: '#f7f8f8', fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Endereco</p>
            {inp('CEP', 'endereco_cep')}
            {inp('Logradouro', 'endereco_logradouro')}
            {inp('Numero', 'endereco_numero')}
            {inp('Complemento', 'endereco_complemento')}
            {inp('Bairro', 'endereco_bairro')}
            {inp('Cidade', 'endereco_cidade')}
            {inp('UF', 'endereco_estado')}
          </div>
          {sel('Status', 'status', [{ value: 'ATIVO', label: 'Ativo' }, { value: 'INATIVO', label: 'Inativo' }])}
          {erro && <div style={{ color: '#f87171', fontSize: 12, marginBottom: 12 }}>âš  {erro}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn-ghost" onClick={() => setModal(null)}>Cancelar</button>
            <button className="btn-primary" onClick={saveCliente} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </Modal>
      }

      {modal === 'contrato' &&
      <Modal title={editId ? 'Editar Contrato' : 'Novo Contrato'} onClose={() => setModal(null)}>
          {inp('Numero do Contrato *', 'codigo_contrato')}
          <p style={{ color: '#8a8f98', fontSize: 11, margin: '-6px 0 12px' }}>
            Informe o numero oficial do contrato. O sistema sugere um codigo inicial, mas o campo pode ser ajustado conforme o documento contratual.
          </p>
          {sel('Cliente *', 'cliente_id', clientes.map((c) => ({ value: (c as any)._id ?? c.id, label: c.razao_social })))}
          {sel('Unidade *', 'unidade_id', unidades.map((u) => ({ value: (u as any)._id ?? u.id, label: u.nome })))}
          {inp('Nome do Contrato *', 'nome_contrato')}
          {inp('Objeto do Contrato', 'objeto_contrato')}
          {inp('Data Início', 'data_inicio', 'date')}
          {inp('Data Fim', 'data_fim', 'date')}
          {inp('Valor Contratado (R$)', 'valor_contratado', 'number')}
          {sel('Status', 'status', [{ value: 'ATIVO', label: 'Ativo' }, { value: 'ENCERRADO', label: 'Encerrado' }, { value: 'SUSPENSO', label: 'Suspenso' }])}
          {erro && <div style={{ color: '#f87171', fontSize: 12, marginBottom: 12 }}>âš  {erro}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn-ghost" onClick={() => setModal(null)}>Cancelar</button>
            <button className="btn-primary" onClick={saveContrato} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </Modal>
      }
    </div>);

}